<script>
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Icon from '$lib/Components/Layout/Icon/Icon.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';

	import attach_rear_corners from './images/attach-rear-corners.svg';

	import attach_to_frame_screwed from './images/attach-to-frame-screwed.svg';
	import attach_to_frame_screwless from './images/attach-to-frame-screwless.svg';

	import final_fastening_drill_holes from './images/final-fastening-drill-holes.svg';
	import frame_assembly from './images/frame-assembly.svg';
	import install_glazing from './images/install-glazing.svg';
	import position_inside_window from './images/position-inside-window.svg';
	import remove_desiccant_chamber_sealing_tape from './images/remove-desiccant-chamber-sealing-tape.svg';
	import unassembled_parts from './images/unassembled-parts.svg';

	const files = [
		{
			title: 'IndowPRO Installation Quick Start Guide',
			href: '/static/routes/how-to/install-indowpro/IndowPRO_Installation_Quick_Start_Guide_Q3_25.pdf',
		},
		{
			title: 'IndowPRO Detailed Installation Manual',
			href: '/static/routes/how-to/install-indowpro/IW-IndowPRO_Installation_Guide-10-20-25.pdf',
		},
		{
			title: 'IndowPRO Product Data Sheet',
			href: '/static/routes/how-to/install-indowpro/IW-IndowPRO-Product_Data-Sheet-08-10-25.pdf',
		},
		{
			title: 'Architectural Details',
			href: '/static/routes/how-to/install-indowpro/IndowPRO_Product_Details_Sheet_Q3_25.pdf',
		},
	];
</script>

<Section>
	<p class="text-left">
		The IndowPRO system is fundamentally designed to provide reliable
		installations. Unlike traditional assembled windows, our modular process
		separates the frame and the glazing, streamlining every step from
		manufacturing to final install. This unique approach slashes shipping costs,
		reduces damage risk, and dramatically simplifies material handling in any
		building. The result is a faster, more predictable process that allows
		installations to occur ahead of schedule and under budget.
	</p>
</Section>

<Section backgroundColor="grey-dark">
	<h2 class="mb-8 text-left">A Smarter Process, A Better Bottom Line</h2>

	<ul
		class="
    grid list-none gap-8
    md:grid-cols-3
  "
	>
		<li>
			<p class="mb-2 text-left font-medium">MAXIMIZE SPEED</p>
			<p class="text-left">
				With frame assembly and installation taking 10-15 minutes per window,
				your crews can complete jobs in a fraction of the time. The lightweight
				frame is easily aligned and installed without the glass, increasing
				speed and safety.
			</p>
		</li>
		<li>
			<p class="mb-2 text-left font-medium">MINIMIZE RISK</p>
			<p class="text-left">
				Our system is engineered for a reliably tight fit, even in the
				out-of-square openings that derail other projects. This real-world
				reliability means fewer callbacks, less wasted material, and more
				predictable outcomes.
			</p>
		</li>
		<li>
			<p class="mb-2 text-left font-medium">SIMPLIFY LOGISTICS</p>
			<p class="text-left">
				Frames and glass move through the building separately. Use specialized
				teams to stage and install frames, reducing the reliance on high-cost
				glaziers for every step and streamlining the entire workflow from the
				truck to the window opening.
			</p>
		</li>
	</ul>
	<p>
		<Button color="blue" href="/commercial/indowpro-dealer-program"
			>IndowPRO Dealer Program</Button
		>
	</p>
</Section>

<Section>
	<h2 class="mb-8 text-left">10 Steps to a Transformed Window</h2>

	<p class="font-medium">
		1) Unpack & Layout: Unpackage and lay out frame components on the floor,
		label side up.
	</p>
	<Image src={unassembled_parts} />
	<p class="font-medium">
		2) Assemble Frame: Slide internal corners into frame pieces and slide PRO
		frame pieces together.
	</p>
	<Image src={frame_assembly} />
	<p class="font-medium">
		3) Attach Rear Corners: Secure the rear corners to complete the frame
		assembly.
	</p>
	<Image src={attach_rear_corners} />
	<p class="font-medium">4) Lift PRO frame into primary frame, align</p>
	<Image src={position_inside_window} />
	<p class="font-medium">
		5) Attach PRO frame to primary frame either with screws or VHB tape.
	</p>
	<div
		class="
    grid w-full gap-6 text-center text-sm
    md:grid-cols-2
  "
	>
		<div>Screwlesss<Image src={attach_to_frame_screwless} /></div>
		<div>Screwed<Image src={attach_to_frame_screwed} /></div>
	</div>
	<p class="font-medium">6) Remove desiccant chamber sealing tape</p>
	<Image src={remove_desiccant_chamber_sealing_tape} />
	<p class="font-medium">7) Install glazing</p>
	<Image src={install_glazing} />
	<p class="font-medium">8) Slide in bottom and top cosmetic cladding pieces</p>
	<p class="font-medium">9) Slide in left and right cosmetic cladding pieces</p>
	<p class="font-medium">
		10) Final Fastening: Drill 4 holes and drive fasteners to secure the
		cosmetic cladding.
	</p>
	<Image src={final_fastening_drill_holes} />
</Section>

<Section backgroundColor="grey-dark">
	<h2 class="mb-8">Downloads for Your Team</h2>
	<ul
		class="
    grid list-none gap-6 text-indow-blue
    md:grid-cols-2
  "
	>
		{#each files as { href, title }, f (f)}
			<li class="flex items-center gap-2">
				<Icon name="file" class="w-6" /><a {href} target="_blank">{title}</a>
			</li>
		{/each}
	</ul>
</Section>

<Section>
	<h2>Build Your Business with IndowPRO</h2>
	<p>
		Glazing Contractors: Ready to add a high-margin, low-hassle window solution
		to your portfolio? We are looking for professional glazing partners to join
		our nationwide network.
	</p>
	<Button href="/about-indow/commercial-sales">Request a Project Review</Button>
	<Button color="white" href="/commercial/indowpro-dealer-program"
		>IndowPRO Dealer Program</Button
	>
</Section>
